import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  Skeleton
} from "@mui/material";
import { getData } from "../../Webservice/Webservice";


const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

function Rewards({ data }) {
  const [rewardsData, setRewardsData] = useState(data);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // if parent passed `data` prop, skip fetching
    if (data) return;

    const fetchRewards = async () => {
      setLoading(true);
      try {
        const tokenValue = localStorage.getItem("website_jwt_token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const resp = await getData("reward/customer", null, token);
        if (resp) {
          // API returns summary + rewards per your example
          setRewardsData(resp);
        }
      } catch (err) {
        console.error("Failed to fetch rewards:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRewards();
  }, [data]);

  const { summary, rewards } = rewardsData || { summary: {}, rewards: [] };

  // Skeleton component for summary cards
  const SummaryCardSkeleton = () => (
    <Grid item
      xs={12} sm={6} md={3}>
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0px 4px 16px rgba(0,0,0,0.15)",
        }}
      >
        <CardContent>
          <Skeleton variant="text" width="80%" height={16} />
          <Skeleton variant="text" width="50%" height={32} sx={{ mt: 1 }} />
        </CardContent>
      </Card>
    </Grid>
  );

  // Skeleton component for reward history items
  const RewardItemSkeleton = () => (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: "0px 4px 8px rgba(0,0,0,0.05)",
        border: "1px solid #eee",
      }}
    >
      <CardContent sx={{ py: 2.5 }}>
        <Grid
          container
          alignItems="center"
          columnSpacing={{ xs: 2, sm: 3, md: 15 }}
          rowSpacing={{ xs: 2 }}
        >
          {/* Order Number & Amount */}
          <Grid item xs={12} sm={4}>
            <Box>
              <Skeleton variant="text" width="80%" height={24} />
              <Skeleton variant="text" width="60%" height={28} sx={{ mt: 0.5 }} />
            </Box>
          </Grid>

          {/* Points Earned / Redeemed */}
          <Grid item xs={12} sm={4}>
            <Stack
              direction="row"
              spacing={4}
              justifyContent="center"
              alignItems="center"
              divider={<Divider orientation="vertical" flexItem />}
              sx={{ py: 1 }}
            >
              <Box textAlign="center">
                <Skeleton variant="text" width={40} height={16} />
                <Skeleton variant="text" width={30} height={24} sx={{ mt: 0.5, mx: 'auto' }} />
              </Box>
              <Box textAlign="center">
                <Skeleton variant="text" width={50} height={16} />
                <Skeleton variant="text" width={30} height={24} sx={{ mt: 0.5, mx: 'auto' }} />
              </Box>
            </Stack>
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={2}>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "space-between", sm: "flex-end" },
                alignItems: "center",
                gap: 3,
              }}
            >
              <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
            </Box>
          </Grid>

          {/* Expires */}
          <Grid item xs={12} sm={2}>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "space-between", sm: "flex-end" },
                alignItems: "center",
                gap: 3,
              }}
            >
              <Box>
                <Skeleton variant="text" width={40} height={16} />
                <Skeleton variant="text" width={60} height={20} sx={{ mt: 0.5 }} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>

      {/* Header */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 500,
          mb: 1,
          color: "#333",
        }}
      >
        Rewards
      </Typography>

      <Typography sx={{ color: "#666", mb: 4 }}>
        Track your earned and redeemed reward points.
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3}>
        {loading ? (
          // Show skeleton cards while loading
          <>
            <SummaryCardSkeleton />
            <SummaryCardSkeleton />
            <SummaryCardSkeleton />
            <SummaryCardSkeleton />
          </>
        ) : (
          // Show actual summary cards
          <>
            {/* Total Earned */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: 3,
                  background: "linear-gradient(135deg, #6EE7B7, #3B82F6)",
                  color: "#fff",
                  boxShadow: "0px 4px 16px rgba(0,0,0,0.15)",
                }}
              >
                <CardContent>
                  <Typography sx={{ fontSize: 13 }}>Total Earned</Typography>
                  <Typography sx={{ fontSize: 28, fontWeight: 500 }}>
                    {summary.totalEarned}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Redeemed */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: 3,
                  background: "linear-gradient(135deg, #FCA5A5, #F87171)",
                  color: "#fff",
                  boxShadow: "0px 4px 16px rgba(0,0,0,0.15)",
                }}
              >
                <CardContent>
                  <Typography sx={{ fontSize: 13 }}>Total Redeemed</Typography>
                  <Typography sx={{ fontSize: 28, fontWeight: 500 }}>
                    {summary.totalRedeemed}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Active Points */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: 3,
                  background: "linear-gradient(135deg, #A78BFA, #6366F1)",
                  color: "#fff",
                  boxShadow: "0px 4px 16px rgba(0,0,0,0.15)",
                }}
              >
                <CardContent>
                  <Typography sx={{ fontSize: 13 }}>Active Points</Typography>
                  <Typography sx={{ fontSize: 28, fontWeight: 500 }}>
                    {summary.activePoints}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Expired Points */}
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  borderRadius: 3,
                  background: "linear-gradient(135deg, #FCD34D, #F59E0B)",
                  color: "#fff",
                  boxShadow: "0px 4px 16px rgba(0,0,0,0.15)",
                }}
              >
                <CardContent>
                  <Typography sx={{ fontSize: 13 }}>Expired Points</Typography>
                  <Typography sx={{ fontSize: 28, fontWeight: 500 }}>
                    {summary.expiredPoints}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Reward History – Horizontal Cards (Same Colors as Your Original Design) */}
      <Box sx={{ mt: 5 }}>
        <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: "#333" }}>
          Reward History
        </Typography>

        <Stack spacing={1}>
          {loading ? (
            // Show skeleton reward items while loading
            <>
              <RewardItemSkeleton />
              <RewardItemSkeleton />
              <RewardItemSkeleton />
            </>
          ) : (
            // Show actual reward items
            rewards.map((item) => (
              <Card
                key={item._id}
                sx={{
                  borderRadius: 2,
                  boxShadow: "0px 4px 8px rgba(0,0,0,0.05)",
                  border: "1px solid #eee",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#f8fafc",
                    borderColor: "#ddd",
                  },
                }}
              >
                <CardContent sx={{ py: 2.5 }}>
                  <Grid
                    container
                    alignItems="center"
                    columnSpacing={{ xs: 2, sm: 3, md: 15 }}
                    rowSpacing={{ xs: 2 }}
                  >
                    {/* Order Number & Amount */}
                    <Grid item xs={12} sm={4}>
                      <Box>
                        <Typography sx={{ fontWeight: 500, fontSize: "1.1rem", color: "#333" }}>
                          {item.orderId.orderNumber}
                        </Typography>
                        <Typography sx={{ color: "#828282ff", fontWeight: 500, fontSize: "1.25rem", mt: 0.5 }}>
                          ₹{item.orderId.totalAmount}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Points Earned / Redeemed */}
                    <Grid item xs={12} sm={4}>
                      <Stack
                        direction="row"
                        spacing={4}
                        justifyContent="center"
                        alignItems="center"
                        divider={<Divider orientation="vertical" flexItem />}
                        sx={{ py: 1 }}
                      >
                        <Box textAlign="center">
                          <Typography sx={{ fontSize: 12, color: "#777" }}>Earned</Typography>
                          <Typography sx={{ fontWeight: 500, fontSize: 22, color: "#777" }}>
                            {item.pointsEarned}
                          </Typography>
                        </Box>
                        <Box textAlign="center">
                          <Typography sx={{ fontSize: 12, color: "#777" }}>Redeemed</Typography>
                          <Typography sx={{ fontWeight: 500, fontSize: 22, color: "#777" }}>
                            {item.pointsRedeemed}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    {/*status */}
                    <Grid item xs={12} sm={2}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: { xs: "space-between", sm: "flex-end" },
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        <Chip
                          label={item.status.toUpperCase()}
                          size="small"
                          sx={{
                            backgroundColor: item.status === "earned" ? "#34D399" : "#60A5FA",
                            color: "#fff",
                            fontWeight: 500,
                            fontSize: 12,
                            textTransform: "uppercase",
                          }}
                        />
                      </Box>
                    </Grid>
                    {/* Status + Expires */}
                    <Grid item xs={12} sm={2}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: { xs: "space-between", sm: "flex-end" },
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        <Box >
                          <Typography sx={{ fontSize: 12, color: "#777" }}>
                            Expires
                          </Typography>
                          <Typography sx={{ fontWeight: 500, color: "#dc2626" }}>
                            {formatDate(item.expiresAt)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))
          )}
        </Stack>

        {/* Empty State */}
        {rewards.length === 0 && (
          <Box sx={{ textAlign: "center", py: 8, color: "#666" }}>
            <Typography variant="h6">No rewards yet</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Your reward history will appear here after your first order.
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
}

export default Rewards;
